import React, { memo, useContext } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { RootState } from '../../../app/rootReducer';
import {
  canEditDecision,
  canReinstateApplication,
  isUnderwriterLiaison,
} from '../../../common/actionHelper';
import { useCaseLock } from '../../../common/caseLocking/CaseLockContext';
import { NoraCaption } from '../../../common/components/NoraCaption';
import { Spacer } from '../../../common/components/v2';
import { DecisionType } from '../../../common/constants/decision';
import { ApplicationDetailedLifecycle } from '../../../common/constants/detailedLifecyle';
import { ApplicationLifecycle } from '../../../common/constants/lifecycle';
import {
  isJuniorUnderwriter,
  isNonCarrierRolePresent,
  NoraPermissionV2,
  Roles,
} from '../../../common/constants/roles';
import { UserOrganization } from '../../../common/constants/userOrganization';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { useAccess } from '../../../common/hooks/useAccess';
import ReviewDecision from '../../detail/drawer/peer-review/ReviewDecision';
import AgreeToReinstate from '../../detail/drawer/reinstatement/AgreeToReinstate';
import DisagreeToReinstate from '../../detail/drawer/reinstatement/DisagreeToReinstate';
import WithdrawReinstatement from '../../detail/drawer/reinstatement/WithdrawReinstatement';
import AgreeToRescind from '../../detail/drawer/rescind/AgreeToRescind';
import DisagreeToRescind from '../../detail/drawer/rescind/DisagreeToRescind';
import { IDecision } from '../../detail/interfaces/IDecision';
import { AuthContext } from '../../login/AuthWrapper';
import ApproveApplication from './icons/ApproveApplication';
import DeclineApplication from './icons/DeclineApplication';
import { DisputeResolved } from './icons/DisputeResolved';
import EditDecision from './icons/EditDecision';
import IneligibleForDecision from './icons/IneligibleForDecision';
import { InitiatePia } from './icons/InitiatePia';
import { InitiateReinstatement } from './icons/InitiateReinstatement';
import OrderCodebacks from './icons/OrderCodebacks';
import OrderEvidences from './icons/OrderEvidences';
import PostponeApplication from './icons/PostponeApplication';
import { TriggerClr } from './icons/TriggerClr';
import { WaiveDecision } from './icons/WaiveDecision';
import styles from './styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

const RUWDetailedLifecycles = [
  ApplicationDetailedLifecycle.RUW,
  ApplicationDetailedLifecycle.RUW_PEER_REVIEW,
  ApplicationDetailedLifecycle.RUW_POST_DECISION,
  ApplicationDetailedLifecycle.COMPLETED_RUW,
];

export const RightSidebarV2 = memo(() => {
  const auth = useContext(AuthContext);
  const { flags } = useContext(FeatureFlagsContext);
  const { isLockedForActions } = useCaseLock();
  const { isPostponeDecisionEnabled } = flags;
  const {
    applicationLifecycle,
    applicationDetailedLifecycle,
    carrierPolicyNumber,
    isAudit,
    activated,
  } = useSelector(state => {
    return {
      applicationLifecycle: state.application.applicationLifecycleV2,
      applicationStage: state.application.applicationStageV2,
      applicationDetailedLifecycle:
        state.application.applicationDetailedLifecycle,
      carrierPolicyNumber: state.application.carrierPolicyNumber,
      isAudit: state.application.isAudit,
      activated: state.application.activated,
    };
  });
  const decisions: IDecision[] = useSelector(state => state.decision.decisions);

  // Derived variable
  const isInvestigation =
    applicationDetailedLifecycle === ApplicationDetailedLifecycle.INVESTIGATION;
  // Covers both INVESTIGATION and COMPLETED_INVESTIGATION — PPWB-migrated cases never get approve/decline
  const isInvestigationLifecycle =
    applicationLifecycle === ApplicationLifecycle.INVESTIGATION;
  const isPIA = applicationLifecycle === ApplicationLifecycle.PIA;
  const isPlanF = applicationLifecycle === ApplicationLifecycle.PLAN_F;
  const isRUWLifeCycle = applicationLifecycle === ApplicationLifecycle.RUW;
  const isReinstatement =
    applicationLifecycle === ApplicationLifecycle.REINSTATEMENT;
  const hasFinalDecision = decisions.some(
    decision => decision.decisionType === DecisionType.FINAL,
  );
  const hasPreliminaryFinalDecision = decisions.some(
    decision => decision.decisionType === DecisionType.PRELIMINARY_FINAL,
  );
  const hasPreliminaryPIADecision = decisions.some(
    decision => decision.decisionType === DecisionType.PRELIMINARY_PIA,
  );
  const piaDecisions = decisions.filter(
    decision => decision.decisionType === DecisionType.PIA && !decision.ignored,
  );
  const planFDecisions = decisions.filter(
    decision =>
      decision.decisionType === DecisionType.PLAN_F && !decision.ignored,
  );
  const isRUW = applicationLifecycle === ApplicationLifecycle.RUW;
  const isAnyRUWFlowLifecycle = RUWDetailedLifecycles.includes(applicationDetailedLifecycle);
  const hasPIADecision = piaDecisions.length > 0;
  const hasPlanFDecision = planFDecisions.length > 0;
  const hasAuditDecision = decisions.some(
    d => d.decisionType === DecisionType.AUDIT,
  );
  // One permission → one check for sidebar, Edit, and Approve/Decline (approve ≡ edit).
  const canMakeDecision = useAccess({
    permission: NoraPermissionV2.MAKE_DECISION,
    legacyCheck: canEditDecision,
  });
  const canMakePreliminaryDecision = useAccess({
    permission: NoraPermissionV2.MAKE_PRELIMINARY_DECISION,
    legacyCheck: isJuniorUnderwriter,
  });
  const canReinstate = useAccess({
    permission: NoraPermissionV2.INITIATE_REINSTATEMENT,
    legacyCheck: canReinstateApplication,
  });
  const canMakeInitialAuditDecision = useAccess({
    permission: NoraPermissionV2.MAKE_INITIAL_AUDIT_DECISION,
    legacyCheck: roles => roles.includes(Roles.CARRIER_UNDERWRITER),
  });
  const canMakeFinalAuditDecision = useAccess({
    permission: NoraPermissionV2.MAKE_FINAL_AUDIT_DECISION,
    legacyCheck: roles => roles.includes(Roles.CHIEF_CARRIER_UNDERWRITER),
  });
  const canMakeLegalDecision = useAccess({
    permission: NoraPermissionV2.MAKE_LEGAL_DECISION,
    legacyCheck: roles => roles.includes(Roles.LEGAL_CARRIER_UNDERWRITER),
  });
  const canInitiatePostIssueAudit = useAccess({
    permission: NoraPermissionV2.INITIATE_POST_ISSUE_AUDIT,
  });

  // For manual CLR trigger - show when completed PIA/Plan F and underwriter halted CLR
  const isCompletedPIA =
    applicationDetailedLifecycle === ApplicationDetailedLifecycle.COMPLETED_PIA;
  const isCompletedPlanFLifecycle =
    applicationDetailedLifecycle ===
    ApplicationDetailedLifecycle.COMPLETED_PLAN_F;
  const latestPiaOrPlanFDecision = [...piaDecisions, ...planFDecisions].sort(
    (a, b) => b.id - a.id,
  )[0];
  const wasClrHalted = latestPiaOrPlanFDecision?.haltClr === true;
  const showManualClrTrigger =
    (isCompletedPIA || isCompletedPlanFLifecycle) &&
    (hasPIADecision || hasPlanFDecision) &&
    wasClrHalted;

  // role related variables
  const hasNonCarrierRole = isNonCarrierRolePresent(auth?.roles);
  const isEthosUser = auth?.userOrganization === UserOrganization.ETHOS;
  const canMakeRescindDecision = Boolean(
    (applicationDetailedLifecycle ===
      ApplicationDetailedLifecycle.CARRIER_AUDIT_REVIEW &&
      canMakeInitialAuditDecision) ||
      (applicationDetailedLifecycle ===
        ApplicationDetailedLifecycle.FINAL_AUDIT_REVIEW &&
        canMakeFinalAuditDecision) ||
      (applicationDetailedLifecycle ===
        ApplicationDetailedLifecycle.HOLDING_FOR_RESCISSION &&
        canMakeLegalDecision),
  );

  function renderCarrierApplication() {
    return (
      <>
        <NoraCaption>application</NoraCaption>
        <AgreeToRescind />
        <DisagreeToRescind />
      </>
    );
  }

  function renderNonCarrierApplication() {
    const canInitiatePia =
      canInitiatePostIssueAudit && activated && !isPIA && !isReinstatement && !hasPIADecision;

    const latestDecision =
      decisions?.length > 0 ? decisions[decisions.length - 1] : null;

    // TODO: Enable edit for plan f decision when ready.
    // const latestDecisionIsPlanF =
    //  latestDecision?.decisionType === DecisionType.PLAN_F;
    const isCompletedPlanF =
      applicationDetailedLifecycle ===
      ApplicationDetailedLifecycle.COMPLETED_PLAN_F;

    const canEdit =
      canMakeDecision &&
      !isAudit &&
      !isCompletedPlanF &&
      !isAnyRUWFlowLifecycle &&
      ((isPIA && hasPIADecision) ||
        // (isPlanF && latestDecisionIsPlanF) ||
        (!isPIA && !isPlanF && hasFinalDecision)) &&
      !isReinstatement;

    const canApproveOrDecline =
      canMakeDecision &&
      !isInvestigationLifecycle &&
      ((!hasFinalDecision && !isAudit) ||
        (isPIA && !hasPIADecision) ||
        (isAudit && !hasAuditDecision) ||
        (isPlanF && !canEdit)); // we create plan f decision when  plan f lifecycle

    const canInitiateReinstatement =
      activated && !isReinstatement && canReinstate;

    const canMakeReinstatementDecision =
      isReinstatement && isEthosUser && canReinstate;

    const isPrelimDecision =
      latestDecision &&
      [DecisionType.PRELIMINARY_FINAL, DecisionType.PRELIMINARY_PIA].includes(
        latestDecision.decisionType,
      );
    const isLatestDecisionUnreviewed =
      latestDecision && latestDecision.reviewStatus === 'Pending';

    return (
      <>
        {canInitiatePia && <InitiatePia policy={carrierPolicyNumber} />}
        <Spacer height={56} />
        {isPrelimDecision && isLatestDecisionUnreviewed && (
          <ReviewDecision decision={latestDecision} />
        )}
        <Spacer height={16} />
        {(!isPrelimDecision || !isLatestDecisionUnreviewed) && (
          <>
            <NoraCaption>application</NoraCaption>
            {canApproveOrDecline && (
              <>
                <ApproveApplication
                  isRUW={isRUW}
                  hasFinalDecision={hasFinalDecision}
                  hasPIADecision={hasPIADecision}
                />
                <DeclineApplication />
                {(isPIA || isAudit || isPlanF) && (
                  <>
                    <IneligibleForDecision />
                    {isPostponeDecisionEnabled && <PostponeApplication />}
                  </>
                )}
              </>
            )}

            {canEdit && (
              <EditDecision
                isRUW={isRUW}
                hasFinalDecision={hasFinalDecision}
                hasPIADecision={hasPIADecision}
              />
            )}
            {isInvestigation && <DisputeResolved />}
          </>
        )}

        {canInitiateReinstatement && <InitiateReinstatement />}

        {canMakeReinstatementDecision && (
          <>
            {!isUnderwriterLiaison(auth.roles) && (
              <>
                <AgreeToReinstate />
                <DisagreeToReinstate />
              </>
            )}
            <WithdrawReinstatement />
          </>
        )}

        {hasFinalDecision && <OrderCodebacks />}
        {(isPIA || isPlanF) && <WaiveDecision />}
        {showManualClrTrigger && <TriggerClr />}
      </>
    );
  }

  function renderJuniorUnderwriterApplication() {
    const canInitiatePia =
      canInitiatePostIssueAudit && activated && !isPIA && !isReinstatement && !hasPIADecision;
    const canApproveOrDecline =
      canMakePreliminaryDecision &&
      !isInvestigationLifecycle &&
      ((isRUWLifeCycle && !hasPreliminaryFinalDecision) ||
        (isPIA && !hasPreliminaryPIADecision));

    const canEdit =
      canMakePreliminaryDecision &&
      !isAudit &&
      (isPIA && hasPreliminaryPIADecision) &&
      !isReinstatement;

    const canInitiateReinstatement =
      activated && !isReinstatement && canReinstate;

    const canMakeReinstatementDecision =
      isReinstatement && isEthosUser && canReinstate;

    return (
      <>
        {canInitiatePia && <InitiatePia policy={carrierPolicyNumber} />}
        <Spacer height={56} />
        <NoraCaption>application</NoraCaption>

        {canApproveOrDecline && (
          <>
            {/* hasFinalDecision and hasPiaDecision are used to auto populate  max coverage,
             hence passing the original decision instead of preliminary decision */}
            <ApproveApplication
              isRUW={isRUW}
              hasFinalDecision={hasFinalDecision}
              hasPIADecision={hasPIADecision}
            />
            <DeclineApplication />
            {isPIA && (
              <>
                <IneligibleForDecision />
                {isPostponeDecisionEnabled && <PostponeApplication />}
              </>
            )}
          </>
        )}

        {canEdit && (
          <EditDecision
            isRUW={isRUW}
            hasFinalDecision={hasPreliminaryFinalDecision}
            hasPIADecision={hasPreliminaryPIADecision}
          />
        )}

        {canInitiateReinstatement && <InitiateReinstatement />}

        {canMakeReinstatementDecision && (
          <>
            {!isUnderwriterLiaison(auth.roles) && (
              <>
                <AgreeToReinstate />
                <DisagreeToReinstate />
              </>
            )}
            <WithdrawReinstatement />
          </>
        )}

        {hasFinalDecision && <OrderCodebacks />}
        {isPIA && <WaiveDecision />}
      </>
    );
  }

  function renderOrderEvidences() {
    return (
      <>
        <NoraCaption>evidences</NoraCaption>
        <OrderEvidences />
      </>
    );
  }

  if (isLockedForActions) {
    return null;
  }

  return (
    <div className={`${styles.Sidebar} ${styles.SidebarRight}`}>
      {renderOrderEvidences()}
      {applicationLifecycle === ApplicationLifecycle.CLR &&
        canMakeRescindDecision &&
        renderCarrierApplication()}

      {canMakePreliminaryDecision && renderJuniorUnderwriterApplication()}

      {!canMakePreliminaryDecision &&
        hasNonCarrierRole &&
        applicationLifecycle !== ApplicationLifecycle.CLR &&
        renderNonCarrierApplication()}
    </div>
  );
});
